import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import jwtDecode from 'jwt-decode';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Segment } from 'semantic-ui-react';
import { renderRoutes, RouteConfigComponentProps } from 'react-router-config';
import { Slide, ToastContainer, toast } from 'react-toastify';
import split from 'lodash/split';
import join from 'lodash/join';
import trim from 'lodash/trim';
import cx from 'classnames';
import { injectIntl, IntlShape } from 'react-intl';

import config from '@plone/volto/registry';
import { PluggablesProvider } from '@plone/volto/components/manage/Pluggable';
import { visitBlocks } from '@plone/volto/helpers/Blocks/Blocks';
import ErrorComponent from '@plone/volto/error';

import {
  Breadcrumbs,
  Footer,
  Header,
  Icon,
  OutdatedBrowser,
  AppExtras,
  SkipLinks,
} from '@plone/volto/components';
import InterRegular from 'volto-ministerio-publico-federal/theme/assets/fonts/inter/Inter-Regular.woff2';
import InterSemiBold from 'volto-ministerio-publico-federal/theme/assets/fonts/inter/Inter-SemiBold.woff2';
import {
  asyncConnect,
  Helmet,
  BodyClass,
  getBaseUrl,
  hasApiExpander,
  isCmsUi,
} from '@plone/volto/helpers';
import {
  getBreadcrumbs,
  getContent,
  getNavigation,
  getTypes,
  getWorkflow,
} from '@plone/volto/actions';
import { getContextNavigation } from '@plone/volto/actions/contextNavigation/contextNavigation';
import { getSlots } from 'mpf-slots-frontend/actions';
import SeoRobots from 'volto-ministerio-publico-federal/components/SEO/SeoRobots';
import {
  contextNavigationParams,
  findContextNavigationBlock,
} from 'volto-ministerio-publico-federal/components/Blocks/ContextNavigation/params';

import { getView } from 'volto-ministerio-publico-federal/helpers';
import { getHomeInfo } from 'volto-ministerio-publico-federal/actions/homeInfo';
import { getPortalNotice } from 'volto-ministerio-publico-federal/actions/portalNotice';
import { loadAnalyticsInfo } from 'volto-ministerio-publico-federal/actions/analytics';

import clearSVG from '@plone/volto/icons/clear.svg';
import MultilingualRedirector from '@plone/volto/components/theme/MultilingualRedirector/MultilingualRedirector';
import WorkingCopyToastsFactory from '@plone/volto/components/manage/WorkingCopyToastsFactory/WorkingCopyToastsFactory';
import LockingToastsFactory from '@plone/volto/components/manage/LockingToastsFactory/LockingToastsFactory';

interface AppProps extends RouteConfigComponentProps {
  pathname: string;
  token?: string | null;
  userId?: string;
  content?: Record<string, any>;
  apiError?: any;
  connectionRefused?: boolean;
  intl: IntlShape; // [cite: 6]
  route?: any;
  staticContext?: any;
}

interface AppState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

interface JwtPayload {
  sub: string;
}

// 3. Componente Principal tipado
export class App extends Component<AppProps, AppState> {
  private mainRef: React.RefObject<HTMLDivElement>;

  constructor(props: AppProps) {
    super(props);
    this.mainRef = React.createRef<HTMLDivElement>();
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: AppProps) {
    if (nextProps.pathname !== this.props.pathname) {
      if (this.state.hasError) {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ hasError: true, error, errorInfo: info });
    config.settings?.errorHandlers?.forEach((handler: (err: Error) => void) =>
      handler(error),
    );
  }

  dispatchContentClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget && this.mainRef.current) {
      const rect = this.mainRef.current.getBoundingClientRect();
      if (event.clientY > rect.bottom) {
        document.dispatchEvent(new Event('voltoClickBelowContent'));
      }
    }
  };

  render(): ReactNode {
    const { views } = config;
    const path = getBaseUrl(this.props.pathname);
    const action = getView(this.props.pathname);
    const isCmsUI = isCmsUi(this.props.pathname);
    const ConnectionRefusedView = views?.errorViews?.ECONNREFUSED;

    const language =
      this.props.content?.language?.token ?? this.props.intl?.locale;

    return (
      <PluggablesProvider>
        {}
        <Helmet>
          <link
            rel="preload"
            as="font"
            type="font/woff2"
            href={InterRegular}
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            as="font"
            type="font/woff2"
            href={InterSemiBold}
            crossOrigin="anonymous"
          />
        </Helmet>

        {language && (
          <Helmet>
            <html lang={language} />
          </Helmet>
        )}

        {}
        <SeoRobots pathname={this.props.pathname} />
        <BodyClass className={`view-${action}view`} />

        {this.props.content?.['@type'] && (
          <BodyClass
            className={`contenttype-${this.props.content['@type']
              .replace(/\s+/g, '-')
              .toLowerCase()}`}
          />
        )}

        <BodyClass
          className={cx({
            [trim(join(split(this.props.pathname, '/'), ' section-'))]:
              this.props.pathname !== '/',
            'siteroot mpf-home-portal mpf-home-portal--active':
              this.props.pathname === '/',
            'is-authenticated': !!this.props.token,
            'is-anonymous': !this.props.token,
            'cms-ui': isCmsUI,
            'public-ui': !isCmsUI,
          })}
        />
        <SkipLinks />
        <Header pathname={path} />
        {path && <Breadcrumbs pathname={path} />}
        <MultilingualRedirector
          pathname={this.props.pathname}
          contentLanguage={this.props.content?.language?.token}
        >
          <Segment
            basic
            className="content-area"
            onClick={this.dispatchContentClick}
          >
            <main ref={this.mainRef} id="mpf-main" className="mpf-main">
              {}
              <span id="inicio" aria-hidden="true" />
              <OutdatedBrowser />
              {this.props.connectionRefused && ConnectionRefusedView ? (
                <ConnectionRefusedView />
              ) : this.state.hasError ? (
                <ErrorComponent
                  message={this.state.error?.message as any}
                  stackTrace={this.state.errorInfo?.componentStack as any}
                />
              ) : (
                renderRoutes(this.props.route?.routes || [], {
                  staticContext: this.props.staticContext,
                })
              )}
            </main>
          </Segment>
        </MultilingualRedirector>
        <Footer />
        <LockingToastsFactory
          content={this.props.content}
          user={this.props.userId}
        />
        <WorkingCopyToastsFactory content={this.props.content} />
        <ToastContainer
          position={toast.POSITION.BOTTOM_CENTER}
          hideProgressBar
          transition={Slide}
          autoClose={5000}
          closeButton={
            <Icon
              className="toast-dismiss-action"
              name={clearSVG}
              size="18px"
            />
          }
        />
        <AppExtras {...this.props} />
      </PluggablesProvider>
    );
  }
}

// 4. Helpers de Dados
export const fetchContent = async ({ store, location }: any) => {
  const content = await store.dispatch(
    getContent(getBaseUrl(location.pathname)),
  );

  const promises: Promise<any>[] = [];
  const { blocksConfig } = config.blocks;

  const visitor = ([id, data]: [string, any]) => {
    const blockType = data?.['@type'];
    const { getAsyncData } = blocksConfig?.[blockType] || {};
    if (getAsyncData) {
      const p = getAsyncData({
        store,
        dispatch: store.dispatch,
        path: location.pathname,
        location,
        id,
        data,
        blocksConfig,
        content,
      });
      if (!p?.length) {
        throw new Error(
          'You should return a list of promises from getAsyncData',
        );
      }
      promises.push(...p);
    }
  };

  visitBlocks(content, visitor);
  await Promise.allSettled(promises);
  return content;
};

// 5. Exports e HOCs
export function connectAppComponent(AppComponent: React.ComponentType<any>) {
  return compose(
    asyncConnect([
      {
        key: 'breadcrumbs',
        promise: ({ location, store: { dispatch } }: any) => {
          if (
            __SERVER__ &&
            !hasApiExpander('breadcrumbs', getBaseUrl(location.pathname))
          ) {
            return dispatch(getBreadcrumbs(getBaseUrl(location.pathname)));
          }
        },
      },
      {
        key: 'content',
        promise: ({ location, store }: any) =>
          __SERVER__ && fetchContent({ store, location }),
      },
      {
        key: 'homeInfo',
        promise: ({ location, store: { dispatch } }: any) =>
          __SERVER__ && location.pathname === '/' && dispatch(getHomeInfo()),
      },
      {
        key: 'portalNotice',
        promise: ({ location, store: { dispatch } }: any) =>
          __SERVER__ &&
          location.pathname === '/' &&
          dispatch(getPortalNotice()),
      },
      {
        // AB#12099: contêiner do GTM para o `Html`, em toda página. Logado
        // não recebe o GTM, então nem pergunta.
        key: 'analytics',
        promise: ({ store }: any) =>
          __SERVER__ &&
          !store.getState().userSession?.token &&
          loadAnalyticsInfo(store.dispatch),
      },
      {
        key: 'navigation',
        promise: ({ location, store: { dispatch } }: any) => {
          if (__SERVER__ && !hasApiExpander('navigation', getBaseUrl('/'))) {
            return dispatch(
              getNavigation(getBaseUrl('/'), config.settings?.navDepth),
            );
          }
        },
      },
      {
        key: 'types',
        promise: ({ location, store: { dispatch } }: any) => {
          if (
            __SERVER__ &&
            !hasApiExpander('types', getBaseUrl(location.pathname))
          ) {
            return dispatch(getTypes(getBaseUrl(location.pathname)));
          }
        },
      },
      {
        key: 'workflow',
        promise: ({ location, store: { dispatch } }: any) =>
          __SERVER__ && dispatch(getWorkflow(getBaseUrl(location.pathname))),
      },
      {
        key: 'contextNavigation',
        promise: ({ location, store: { dispatch } }: any) => {
          if (!__SERVER__) {
            return;
          }
          const path = getBaseUrl(location.pathname);
          return dispatch(getSlots(path))
            .then((slots: any) => {
              const block = findContextNavigationBlock(slots?.items);
              return (
                block &&
                dispatch(
                  getContextNavigation(path, contextNavigationParams(block)),
                )
              );
            })
            .catch(() => null);
        },
      },
    ]),
    injectIntl,
    connect(
      (state: any, props: any) => ({
        pathname: props.location.pathname,
        token: state.userSession?.token,
        userId: state.userSession?.token
          ? (jwtDecode(state.userSession.token) as JwtPayload).sub
          : '',
        content: state.content?.data,
        apiError: state.apierror?.error,
        connectionRefused: state.apierror?.connectionRefused,
      }),
      null,
    ),
  )(AppComponent);
}

export default connectAppComponent(App);
